import { memo } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

interface ManageDocumentsHeaderProps {
  name: string;
  previousCount: number;
  submitting: boolean;
  whatsAppLabel?: string;
  onWhatsApp?: () => void;
  onClose: () => void;
  onOpenPrevious: () => void;
}

export const ManageDocumentsHeader = memo(function ManageDocumentsHeader({
  name,
  previousCount,
  submitting,
  whatsAppLabel,
  onWhatsApp,
  onClose,
  onOpenPrevious,
}: ManageDocumentsHeaderProps) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        borderRadius: 1.5,
        border: 1,
        borderColor: "grey.200",
        bgcolor: "grey.50",
        px: { xs: 1, sm: 1.25 },
        py: 0.75,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        flexWrap="wrap"
        useFlexGap
      >
        <Tooltip title="Voltar para a lista">
          <span>
            <IconButton
              onClick={onClose}
              disabled={submitting}
              size="small"
              aria-label="Voltar"
              sx={{
                color: "text.secondary",
                minWidth: 40,
                minHeight: 40,
                "&:hover": {
                  bgcolor: "background.paper",
                  color: "primary.main",
                },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ mx: 0.25, my: 0.75, borderColor: "grey.300" }}
        />

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{ minWidth: 0, flex: 1, pl: 0.25 }}
        >
          <DescriptionOutlinedIcon
            sx={{ fontSize: 26, color: "#00A884", flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.2, display: "block", letterSpacing: 0.2 }}
            >
              Documentos e liberação
            </Typography>
            <Typography
              variant="subtitle1"
              component="h2"
              fontWeight={600}
              noWrap
              sx={{ lineHeight: 1.3 }}
            >
              {name}
            </Typography>
          </Box>
        </Stack>

        {onWhatsApp && whatsAppLabel ? (
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<WhatsAppIcon />}
            onClick={onWhatsApp}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              minHeight: 40,
              whiteSpace: "nowrap",
            }}
          >
            {whatsAppLabel}
          </Button>
        ) : null}

        <Badge
          badgeContent={previousCount}
          color="default"
          sx={{
            flexShrink: 0,
            ml: { xs: 0.5, sm: 1 },
            "& .MuiBadge-badge": {
              fontWeight: 600,
              bgcolor: "grey.300",
              color: "text.primary",
            },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<HistoryOutlinedIcon fontSize="small" />}
            onClick={onOpenPrevious}
            sx={{
              textTransform: "none",
              borderColor: "grey.300",
              bgcolor: "background.paper",
              color: "text.primary",
              whiteSpace: "nowrap",
              minHeight: 40,
              pr: 2.5,
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "background.paper",
              },
            }}
          >
            Liberações anteriores
          </Button>
        </Badge>
      </Stack>
    </Box>
  );
});
